import type { CodeableConcept, Patient, Reference } from '@medplum/fhirtypes'
import { codeableConcept } from '@seen/core/codeableConcept'
import { invariant } from '@seen/core/invariant'
import { ExtendedObservation } from '@seen/core/models/observation'
import { type JSX, useCallback, useRef } from 'react'
import { toast } from 'sonner'
import { FirstMatchDecorator } from '#src/components/rich-text-editor/ObservationNode/FirstMatchDecorator.ts'
import { ObservationNodeFrame } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeFrame.tsx'
import {
  CHOOSE_PARTICIPANT,
  isForeignRecord,
} from '#src/components/rich-text-editor/ObservationNode/observationSubject.ts'
import { ObservationVitalInput } from '#src/components/rich-text-editor/ObservationNode/ObservationVitalInput.tsx'
import { ObservationVitalNote } from '#src/components/rich-text-editor/ObservationNode/ObservationVitalNote.tsx'
import type {
  ObservationCaptureTarget,
  ObservationNodeAttributes,
} from '#src/components/rich-text-editor/ObservationNode/types.ts'
import { useNodeRecord } from '#src/components/rich-text-editor/ObservationNode/useNodeRecord.ts'
import { withClinicianNote } from '#src/components/rich-text-editor/ObservationNode/vitalNote.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useDebouncedCallback } from '#src/hooks/useDebouncedCallback.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { extractTextFromHTML } from '#src/utils/html.ts'

export function ObservationNodePain({
  attributes,
  updateAttributes,
  participant,
  noParticipant = false,
  editable = true,
  recordStatus,
}: {
  attributes: ObservationNodeAttributes
  updateAttributes: (attributes: Partial<ObservationNodeAttributes>) => void
  participant: Reference<Patient> | undefined
  // No participant chosen: nothing can be recorded.
  noParticipant?: boolean
  // Called by the node's record once a first answer is on the node.
  onReferencePersisted?: () => void
  // Whether the note can be changed, and so the vital cleared.
  editable?: boolean
  recordStatus?: ObservationCaptureTarget['recordStatus']
}): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const record = useNodeRecord()
  const { observation } = record
  // Recorded for someone else than the editor's participant: shown, and
  // clearable, but not changed from here.
  const foreign = isForeignRecord(
    observation,
    noParticipant ? null : participant
  )
  // A note that cannot be changed (signed, posted, read-only) takes no
  // answer here, as every observation control.
  const locked = !editable || isReadOnly || noParticipant || foreign

  // A note written before the vital is recorded, for its record.
  const pendingNote = useRef('')
  const createObservationPromiseRef =
    useRef<Promise<ExtendedObservation> | null>(null)

  const practitioner = useCurrentPractitioner()

  const updatePain = useCallback(
    async (observation: ExtendedObservation, html: string) => {
      const value = extractPainFromHtml(html)
      const painCodeableConcept =
        value === undefined ? undefined : PAIN_CODEABLE_CONCEPT_BY_NUMBER[value]
      try {
        await record.patch((o) => {
          o.valueCodeableConcept = painCodeableConcept
          o.setNoteFirst(html, practitioner.reference)
        }, observation)
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? `Failed to update pain: ${error.message}`
            : `Failed to update pain`
        toast.error(errorMessage)
        throw error
      }
    },
    [record, practitioner.reference]
  )

  const debouncedOnChange = useDebouncedCallback(async (html) => {
    if (!attributes.reference && !attributes.draft) {
      if (createObservationPromiseRef.current) {
        // Wait for the existing createObservationPromiseRef to finish before proceeding
        const createdObservation = await createObservationPromiseRef.current
        await updatePain(createdObservation, html)
      } else {
        const value = extractPainFromHtml(html)
        const painCodeableConcept =
          value === undefined
            ? undefined
            : PAIN_CODEABLE_CONCEPT_BY_NUMBER[value]
        if (painCodeableConcept === undefined) {
          toast.error(`Enter a number from 0 to 10 for pain.`)
          return
        }
        createObservationPromiseRef.current = record.create(
          withClinicianNote(
            pendingNote.current,
            practitioner.reference,
            ExtendedObservation.fromValueCodeableConcept({
              valueCodeableConcept: painCodeableConcept,
              code: codeableConcept({
                code: attributes.code,
                system: attributes.system,
                display: attributes.codingDisplay,
              }),
              participant,
              practitioner: practitioner.reference,
              note: html,
              status: recordStatus,
            })
          )
        )
        try {
          await createObservationPromiseRef.current
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? `Failed to submit pain: ${error.message}`
              : `Failed to submit pain`
          toast.error(errorMessage)
          throw error
        } finally {
          createObservationPromiseRef.current = null
        }
      }
    } else {
      invariant(observation, 'observation is required')
      await updatePain(observation, html)
    }
  }, 500) // Reduced from 2500ms to 500ms to mitigate race condition where users submit timeline notes before observation reference is created. Still debounced to avoid excessive version history entries, and we flush on blur as well.

  const handleAutofocused = useCallback(() => {
    updateAttributes({ autofocus: false })
  }, [updateAttributes])

  return (
    <ObservationNodeFrame
      attributes={attributes}
      label={ExtendedObservation.getNickname(
        attributes.code,
        attributes.codingDisplay
      )}
      className="relative"
      noHover
      // The line itself is the value's editor; a second one in the details
      // would write a value this line does not show.
      valueInDetails={false}
      participant={participant}
      foreign={foreign}
      blockedReason={
        noParticipant && !observation ? CHOOSE_PARTICIPANT : undefined
      }
      updateAttributes={editable ? updateAttributes : undefined}
    >
      <ObservationVitalInput
        code={attributes.code}
        value={observation?.noteFirstText ?? ''}
        debouncedOnChange={debouncedOnChange}
        additionalExtensions={additionalExtensions}
        autofocus={attributes.autofocus}
        onAutofocused={handleAutofocused}
        disabled={locked}
        noValue={!editable && !attributes.reference}
      />
      <ObservationVitalNote
        observation={observation}
        onPendingChange={(note) => {
          pendingNote.current = note
        }}
        disabled={locked}
      />
    </ObservationNodeFrame>
  )
}

/**
 * Matches an integer from 0 to 10 (inclusive) with these rules:
 * - Must NOT be part of a larger integer (blocks "11", "110", "35")
 * - Must NOT be part of a decimal (blocks "3.5", "10.2", ".5")
 * - Allows a trailing period ONLY if it is not part of a decimal (allows "4.")
 * - Disallows negatives
 * How it works:
 * (?<![-\d.])       The char before cannot be '-', digit, or '.'
 *                   → blocks "-3", "13", "3.5", ".5"
 * (10|[0-9])        The integer itself
 * (?!\d)            Next char cannot be a digit
 *                   → blocks "11", "110"
 * (?!\.\d)          Next chars cannot be ".<digit>"
 *                   → blocks decimals like "3.5", "10.2"
 *                   → but still allows "4." because '.' not followed by digit
 */
const ZERO_TO_TEN_REGEX = /(?<![-\d.])(10|[0-9])(?!\d)(?!\.\d)/

const additionalExtensions = [
  FirstMatchDecorator.configure({
    regex: ZERO_TO_TEN_REGEX,
    className: 'font-mono tabular-nums text-foreground',
  }),
]

function extractPainFromHtml(html: string): number | undefined {
  const text = extractTextFromHTML(html)

  const match = text.match(ZERO_TO_TEN_REGEX)

  if (!match) {
    return undefined
  }

  return Number(match[1])
}

interface PainCodeableConceptByNumber {
  [score: number]: CodeableConcept | undefined
}

const PAIN_CODEABLE_CONCEPT_BY_NUMBER: PainCodeableConceptByNumber = {
  0: codeableConcept({
    code: 'LA19102-5',
    display: '0',
  }),
  1: codeableConcept({
    code: 'LA6112-2',
    display: '1',
  }),
  2: codeableConcept({
    code: 'LA6113-0',
    display: '2',
  }),
  3: codeableConcept({
    code: 'LA6114-8',
    display: '3',
  }),
  4: codeableConcept({
    code: 'LA6115-5',
    display: '4',
  }),
  5: codeableConcept({
    code: 'LA10137-0',
    display: '5',
  }),
  6: codeableConcept({
    code: 'LA10138-8',
    display: '6',
  }),
  7: codeableConcept({
    code: 'LA10139-6',
    display: '7',
  }),
  8: codeableConcept({
    code: 'LA10140-4',
    display: '8',
  }),
  9: codeableConcept({
    code: 'LA10141-2',
    display: '9',
  }),
  10: codeableConcept({
    code: 'LA21948-7',
    display: '10',
  }),
} as const
